// pages/Dashboard/GarageFinances.tsx
import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analyticsService';
import { FinancialOverview } from './Finances/FinancialOverview';
import { PaymentBreakdown } from './Finances/PaymentBreakdown';
import { FinancialChart } from './Finances/FinancialChart';
import { SettlementHistory } from './Finances/SettlementHistory';
import { RefreshCw, DollarSign } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';
import { InvoicesList } from './Finances/InvoicesList';

export const GarageFinances = () => {
    const { t, isRTL } = useLanguage();
    const [finances, setFinances] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [period, setPeriod] = useState('all'); // all, week, month, year

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const garageId = user._id;
    const consumeRate = 0
    useEffect(() => {
        loadFinances();
    }, [period]);

    const loadFinances = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await analyticsService.getFinances(period);
            setFinances(response.data);
        } catch (error) {
            console.error('Erreur lors du chargement des finances:', error);
            setError(t('finances.messages.error'));
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 sm:p-8">
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className={`p-3 sm:p-6 space-y-4 sm:space-y-6 bg-gray-50 min-h-screen ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                    <h1 className="text-xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
                        <DollarSign className="text-indigo-600 flex-shrink-0" size={24} />
                        {t('finances.title')}
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                        {t('finances.subtitle')}
                    </p>
                </div>
                <div className={`flex items-center gap-2 sm:gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <select
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                    >
                        <option value="all">{t('finances.periods.all')}</option>
                        <option value="week">{t('finances.periods.week')}</option>
                        <option value="month">{t('finances.periods.month')}</option>
                        <option value="year">{t('finances.periods.year')}</option>
                    </select>

                    <button
                        onClick={loadFinances}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
                        title={t('finances.actions.refresh')}
                    >
                        <RefreshCw className="h-5 w-5 text-gray-600" />
                    </button>
                </div>
            </div>

            {finances && (
                <>
                    <FinancialOverview data={finances} />

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        <PaymentBreakdown data={finances} />
                    </div>

                    <FinancialChart data={finances.evolution} />

                    <SettlementHistory settlements={finances.settlements} />
                    <InvoicesList garageId={garageId} commissionRate={consumeRate} />
                </>
            )}
        </div>
    );
};